// ---------------------------------------------------------------------------
// What the wizard does when it is left (R17b)
//
// The router runs the wizard's leave hook before every navigation away (the
// nav, links, goTo and, since R12c, back/forward). Navigation is never
// blocked; the hook only decides what happens to the draft on the way out.
// The decision, taken out of pages/campaign-wizard.ts unchanged; the page
// carries it out (saves, asks, commits the Sales Rep change, starts the
// regeneration). Plain TypeScript.
// ---------------------------------------------------------------------------

/**
 * - `leave`: nothing to do (the wizard finished, there is nothing in it, or
 *   the edited campaign has not loaded);
 * - `save`: the draft is saved already (or was just created by its autosave),
 *   so it is saved again silently; for a campaign past 'draft' the page then
 *   commits a valid Sales Rep change still waiting for its debounce and starts
 *   the message regeneration the visit queued (`appliesOnLeave`);
 * - `ask`: a new draft with something in it asks "Save draft?".
 */
export type LeaveAction = 'leave' | 'save' | 'ask';

export interface LeaveState {
  /** "Finish setup" or "Back to Campaign" was pressed. */
  finished: boolean;
  /**
   * An edited campaign that has not loaded: still loading, or its load failed
   * (logic/load.ts). The draft is the empty one every wizard starts with, not
   * the campaign; saved on the way out it went over the campaign (found in
   * R17b), so nothing is saved (R17a3).
   */
  notLoaded: boolean;
  /**
   * The draft is known to the server: an edited campaign (from the start, even
   * before it has loaded), or a new one its first save created.
   */
  saved: boolean;
  /** hasMeaningfulData(draft) (logic/draft.ts). */
  meaningful: boolean;
}

export function leaveAction(state: LeaveState): LeaveAction {
  // Campaign was already finished — nothing to save.
  if (state.finished) return 'leave';
  // Not the campaign yet (or never): nothing of it to save, and the empty draft must not go over it.
  if (state.notLoaded) return 'leave';
  // Already persisted (incl. just auto-created) — save silently.
  if (state.saved) return 'save';
  if (!state.meaningful) return 'leave';
  return 'ask';
}

/**
 * After the silent save: a campaign past 'draft' with an id commits its waiting
 * Sales Rep change and starts the regeneration. A valid Sales-Rep change made
 * in the last debounce window would otherwise be cancelled by the effect's
 * cleanup on unmount and silently lost.
 */
export const appliesOnLeave = (editingNonDraft: boolean, savedId: string | null | undefined): boolean =>
  editingNonDraft && !!savedId;

/** One run that callers share while it is out, and a way to forget it. */
export interface SharedRun<T> {
  /** The run still out, or a new one from `start`. */
  run(start: () => Promise<T>): Promise<T>;
  /** Forgets a run still out: a wizard that mounts starts with none. */
  reset(): void;
}

/**
 * One leave at a time: a leave asked for while one is out gets that one (two
 * at once share one "Save draft?"), and once it has settled the next leave is
 * a new one.
 *
 * The run is forgotten once it has settled, and only if it is still the one
 * kept (R17a3). The wizard kept its leave in flight and cleared it in the
 * leave's own `finally`: a leave that decided at once (a finished wizard,
 * nothing to save) ran that `finally` before the line that kept it, so it
 * stayed kept, settled, and every later leave in the tab returned it and did
 * nothing — no save, no "Save draft?", no regeneration (found in R17b).
 */
export function sharedRun<T>(): SharedRun<T> {
  let current: Promise<T> | null = null;
  return {
    run(start) {
      if (current) return current;
      const running = start();
      current = running;
      const forget = () => {
        if (current === running) current = null;
      };
      running.then(forget, forget);
      return running;
    },
    reset() {
      current = null;
    },
  };
}

/** The question for a new draft with something in it. */
export const SAVE_DRAFT_QUESTION = {
  message: 'Do you want to save this campaign as a draft?',
  title: 'Save draft?',
  confirmLabel: 'Save',
} as const;

/** The one toast that replaces a toast per edit when the regeneration starts on the way out. */
export const regenerationStartedMessage = (queued: number): string =>
  `Applying your changes — regenerating ${queued} message${queued === 1 ? '' : 's'}…`;
